import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { createSupplierContact } from './createSupplierContact';
import { type CreateSupplierContactMutationOptions } from './types';

/**
 * React Query based custom hook for creating a new contact for a supplier.
 *
 * Invalidates the supplier detail query for the affected data exchange ID on settle.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-details/operation/create_supplier_contact_suppliers_details__de_id__contacts_post
 */
export const useMutationCreateSupplierContact = (
  options?: CreateSupplierContactMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deId, payload }) =>
      createSupplierContact(deId, payload),
    onSettled(_data, _error, { deId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getDetail(deId),
      });
    },
    throwOnError: false,
    ...options,
  });
};
